import { useEffect, useState } from 'react';

interface ERC20Balance {
  symbol: string;
  balance: string;
  decimals: number;
}

interface wHANDYBalance {
  symbol: string;
  balance: string;
  balanceRaw: string;
  contract: string;
}

interface Transaction {
  hash: string;
  from: string | null;
  to: string | null;
  value: string;
  blockNumber: number;
  timestamp: number;
}

export function useWalletData(walletAddress?: string, apiBase = '') {
  const [avaxBalance, setAvaxBalance] = useState<string>('-');
  const [erc20Balances, setErc20Balances] = useState<ERC20Balance[]>([]);
  const [vttnBalance, setVttnBalance] = useState<wHANDYBalance | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // AVAX 잔고 조회
  async function loadAvaxBalance(address: string) {
    try {
      const response = await fetch(`${apiBase}/api/blockchain/balance/${address}`);
      if (response.ok) {
        const data = await response.json();
        setAvaxBalance(data.balance);
      } else if (response.status === 404) {
        // 404는 조용히 처리 (에러 메시지 표시 안 함)
        console.log('AVAX 잔고 조회: 엔드포인트 없음 (404)');
      }
    } catch (error) {
      // 네트워크 오류만 로그 (에러 메시지 표시 안 함)
      console.log('AVAX 잔고 조회 실패 (무시):', error);
    }
  }

  // ERC20 토큰 잔고 조회 (현재는 사용하지 않음 - wHANDY만 사용)
  async function loadERC20Balances(address: string) {
    // wHANDY만 사용하므로 ERC20 조회 비활성화
    setErc20Balances([]);
  }

  // wHANDY 토큰 잔고 조회 (새로운 ERC20 API 사용)
  async function loadwHANDYBalance(address: string) {
    try {
      // wHANDY 토큰 주소 사용
      const vttnTokenAddress = '0xd4752118B3ae3b31fE28B33435d9Ef4b1B93000E';
      const response = await fetch(`${apiBase}/api/blockchain/erc20/balance?token=${vttnTokenAddress}&address=${address}`);
      if (response.ok) {
        const data = await response.json();
        setVttnBalance({
          symbol: data.symbol || 'wHANDY',
          balance: data.balance || '0.0',
          balanceRaw: data.balance || '0',
          contract: data.token || vttnTokenAddress
        });
      } else {
        // API 실패 시 DB에서 조회
        const dbResponse = await fetch(`${apiBase}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('handy-jwt-token')}`
          }
        });
        if (dbResponse.ok) {
          const userData = await dbResponse.json();
          setVttnBalance({
            symbol: 'wHANDY',
            balance: userData.balance?.toString() || '0.0',
            balanceRaw: userData.balance?.toString() || '0',
            contract: vttnTokenAddress
          });
        }
      }
    } catch (error) {
      console.error('wHANDY 잔고 조회 실패:', error);
      // 에러 시 기본값 설정
      setVttnBalance({
        symbol: 'wHANDY',
        balance: '0.0',
        balanceRaw: '0',
        contract: '0xd4752118B3ae3b31fE28B33435d9Ef4b1B93000E'
      });
    }
  }

  // 트랜잭션 히스토리 조회
  async function loadTransactions(address: string) {
    try {
      console.log('🔍 트랜잭션 조회 시작:', address);
      // 통합 내역을 먼저 본다. 온체인 스캔만 쓰면 게임 베팅·당첨·상금처럼
      // 원장만 움직이는 건이 하나도 안 보였다. (2026-10-16)
      const t = localStorage.getItem('handy-jwt-token');
      if (t) {
        const u = await fetch(`${apiBase}/api/wallet-history?limit=60`, {
          headers: { Authorization: `Bearer ${t}` },
        }).then(r => r.ok ? r.json() : null).catch(() => null);
        if (u?.success && Array.isArray(u.transactions)) {
          setTransactions(u.transactions);
          return;
        }
      }
      // 통합 내역이 안 되면 기존 온체인 스캔으로 물러난다
      const response = await fetch(`${apiBase}/api/blockchain/txs/${address}?blocks=18000&limit=50`);
      console.log('📡 API 응답 상태:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('✅ 트랜잭션 데이터:', data);
        console.log('📊 거래 건수:', data.transactions?.length || 0);
        setTransactions(data.transactions || []);
      } else if (response.status === 404) {
        // 404는 조용히 처리 (에러 메시지 표시 안 함)
        console.log('트랜잭션 조회: 엔드포인트 없음 (404)');
      }
    } catch (error) {
      // 네트워크 오류만 로그 (에러 메시지 표시 안 함)
      console.log('트랜잭션 조회 실패 (무시):', error);
    }
  }

  // wHANDY 토큰 전송
  async function sendwHANDY(to: string, amount: string, privateKey: string) {
    try {
      const response = await fetch(`${apiBase}/api/blockchain/vttn/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to,
          amount,
          privateKey
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // 전송 후 잔고 업데이트
        if (walletAddress) {
          await loadwHANDYBalance(walletAddress);
          await loadTransactions(walletAddress);
        }
        return { success: true, txHash: data.txHash };
      } else {
        const error = await response.json();
        return { success: false, error: error.message };
      }
    } catch (error) {
      console.error('wHANDY 전송 실패:', error);
      return { success: false, error: 'wHANDY 전송 중 오류가 발생했습니다.' };
    }
  }

  // 모든 지갑 데이터 로드
  async function loadWalletData(address: string) {
    if (!address) return;
    
    setIsLoading(true);
    try {
      await Promise.all([
        loadAvaxBalance(address),
        loadERC20Balances(address),
        loadwHANDYBalance(address),
        loadTransactions(address)
      ]);
    } finally {
      setIsLoading(false);
    }
  }

  // 지갑 주소 변경 시 데이터 로드
  useEffect(() => {
    console.log('🔄 useWalletData - walletAddress 변경:', walletAddress);
    if (walletAddress) {
      console.log('✅ 지갑 데이터 로드 시작');
      loadWalletData(walletAddress);
    } else {
      console.log('⚠️ walletAddress 없음 - 데이터 로드 안 함');
    }
  }, [walletAddress]);

  // 수동 새로고침
  function refresh() {
    if (walletAddress) {
      loadWalletData(walletAddress);
    }
  }

  return {
    avaxBalance,
    erc20Balances,
    vttnBalance,
    transactions,
    isLoading,
    refresh,
    sendwHANDY
  };
}
